import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/queryKeys'
import * as valueTagsApi from '@/api/valueTags'
import type { ValueTag } from '@/types'

export function useValueTags(): ValueTag[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.valueTags.all(),
    queryFn: valueTagsApi.fetchValueTags,
  })
  return data
}

export function useValueTagsWithCounts(): { tag: ValueTag; count: number }[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.valueTags.withCounts(),
    queryFn: valueTagsApi.fetchValueTagsWithCounts,
  })
  return data
}

export function useTagsForPriority(priorityId: string): ValueTag[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.valueTags.forPriority(priorityId),
    queryFn: () => valueTagsApi.fetchTagsForPriority(priorityId),
    enabled: !!priorityId,
  })
  return data
}

function invalidateValueTags(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['valueTags'] })
}

export function useCreateValueTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: valueTagsApi.createValueTag,
    onSuccess: () => invalidateValueTags(qc),
  })
}

export function useDeleteValueTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: valueTagsApi.deleteValueTag,
    onSuccess: () => invalidateValueTags(qc),
  })
}

export function useAddTagToPriority() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ priorityId, tagId }: { priorityId: string; tagId: string }) =>
      valueTagsApi.addTagToPriority(priorityId, tagId),
    onSuccess: () => invalidateValueTags(qc),
  })
}

export function useRemoveTagFromPriority() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ priorityId, tagId }: { priorityId: string; tagId: string }) =>
      valueTagsApi.removeTagFromPriority(priorityId, tagId),
    onSuccess: () => invalidateValueTags(qc),
  })
}

export function useCreateAndAssignTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ priorityId, label }: { priorityId: string; label: string }) =>
      valueTagsApi.createAndAssignTag(priorityId, label),
    onSuccess: () => invalidateValueTags(qc),
  })
}

export function useRenameValueTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, label }: { id: string; label: string }) => valueTagsApi.renameValueTag(id, label),
    onSuccess: () => invalidateValueTags(qc),
  })
}
